import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { isBillingError as isBillingErrorV2, isUnpaid as isUnpaidV2 } from '../utils/billing';
import { isBillingError, isUnpaid } from '../utils/billingV3';
import { areEqual } from '../utils/iterable/areEqual';
import { shops } from '../utils/selectors';
import { toJS } from '../utils/iterable/toJS';
import { get } from '../utils/iterable/get';

import { BETA_CHANNELS, FEATURE } from '../constants/features';
import { FAIRE } from '../constants/channels';
import { EMPTY } from '../constants';
import { PLAN } from '../constants/billing';

export const SubscriptionContext = createContext({ error: false, unpaid: false });
SubscriptionContext.displayName = 'SubscriptionContext';

export default function SubscriptionProvider({ children }) {
  const currentShopId = useSelector(shops.currentShopId);
  const userShops = useSelector(shops.userShops, areEqual);
  const userHasShops = useSelector(shops.userHasShops);
  const userLoaded = useSelector(get(['user', 'loaded']));
  const subscriptions = useSelector(get(['user', 'subscriptions'])) || EMPTY.MAP;
  const billingV3Enabled = useSelector(get(['user', 'features', FEATURE.BILLING_V3]));
  const value = useMemo(
    function factory() {
      const result = { error: false, unpaid: false };

      if (userLoaded && userHasShops) {
        const shop = userShops.getIn(['byId', currentShopId], EMPTY.MAP);

        if (
          !shop ||
          shop.get('isVelaOnlyShop') ||
          BETA_CHANNELS.has(shop.get('channel')) ||
          (shop.get('channel') === FAIRE && !billingV3Enabled)
        ) {
          return result;
        }

        const subscription = subscriptions.get(currentShopId);

        if (!subscription) {
          result.error = true;
          Sentry.captureException(
            'Subscription not found',
            {
              extra: {
                shops: toJS(userShops.get('byId')),
                subscriptions: toJS(subscriptions),
              },
            }
          );
        } else if (billingV3Enabled) {
          result.error = isBillingError({
            syncStatus: shop.get('syncStatus'),
            plan: subscription.get('plan', PLAN.ERROR),
          });

          result.unpaid = !result.error && isUnpaid(subscription);
        } else {
          result.error = isBillingErrorV2({
            syncStatus: shop.get('syncStatus'),
            plan: subscription.get('plan', PLAN.ERROR),
          });

          result.unpaid = !result.error && isUnpaidV2(subscription);
        }
      }

      return result;
    },
    [billingV3Enabled, currentShopId, subscriptions, userHasShops, userLoaded, userShops]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

SubscriptionProvider.propTypes = {
  children: PropTypes.node,
};
